import { Injectable, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import {
  PerformanceClassificationStatus,
  TrainPerformanceClassificationColor,
  TrainPerformanceClassificationSeverity,
  TrainPerformanceClassificationText,
  TrainStoppageLabel,
  TrainStoppageColor,
  TrainStoppageSeverity,
  TrainLengthClassificationColor,
  TrainLengthClassificationStatus,
  FiltersPreferences
} from 'src/app/models/train-info.model';

@Injectable({
  providedIn: 'root'
})
export class IOHFilterFormService {
  iohFilterFormGroup: FormGroup = new FormGroup({});
  refreshFilterView$ = new BehaviorSubject<boolean>(false);
  //notifies listeners once the initial train data has loaded and the filter form has been rebuilt
  dataReady$ = new Subject<void>();
  initialValues = null;
  isIohFilterApplied = false;
  commodityFilterOptions = [];
  iohCommodityAllFilterOptions = [];
  isCommodityApiFailed = false;
  iohCommodityApiErrorCode = '';
  isTrainGroupApiFailed = false;
  trainGroupsApiErrorCode = '';

  trainLengthOptions = [
    {
      label: '7,000 feet or Less',
      value: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_1,
      color: TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_1,
      length: [],
      filterKey: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_1
    },
    {
      label: '7,001 to 8,000 feet',
      value: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_2,
      color: TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_2,
      length: [TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_2],
      filterKey: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_2
    },
    {
      label: '8,001 to 10,000 feet',
      value: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_3,
      color: TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_3,
      length: [
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_2,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_3
      ],
      filterKey: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_3
    },
    {
      label: '10,001 to 12,000 feet',
      value: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_4,
      color: TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_4,
      length: [
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_2,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_3,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_4
      ],
      filterKey: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_4
    },
    {
      label: '12,001 feet or More',
      value: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_5,
      color: TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_5,
      length: [
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_2,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_3,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_4,
        TrainLengthClassificationColor.REPORTED_LENGTH_CLASSIFICATION_5
      ],
      filterKey: TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_5
    }
  ];

  trainPerformanceOptions = [
    {
      label:
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME_LABEL,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME
      ),
      color:
        TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME,
      severity:
        TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME,
      filterKey: PerformanceClassificationStatus.AOT
    },
    {
      label:
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_EARLY,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_EARLY
      ),
      color:
        TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_EARLY,
      severity:
        TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_EARLY,
      filterKey: PerformanceClassificationStatus.EARLY
    },
    {
      label:
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_DELAYED,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_DELAYED
      ),
      color:
        TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_DELAYED,
      severity:
        TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_DELAYED,
      filterKey: PerformanceClassificationStatus.DELAYED
    },
    {
      label: TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE
      ),
      color:
        TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_LATE,
      severity:
        TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_LATE,
      filterKey: PerformanceClassificationStatus.LATE,
      hasSubFilters: true
    }
  ];

  lateSubFilterOptions = [
    {
      label: TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_2,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_2
      ),
      color: TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_LATE_2,
      severity: TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_LATE_2,
      filterKey: PerformanceClassificationStatus.LATE_2,
      parentKey: PerformanceClassificationStatus.LATE,
      timeRange: '0-240'
    },
    {
      label: TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_4,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_4
      ),
      color: TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_LATE_4,
      severity: TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_LATE_4,
      filterKey: PerformanceClassificationStatus.LATE_4,
      parentKey: PerformanceClassificationStatus.LATE,
      timeRange: '241-480'
    },
    {
      label: TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_8,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_8
      ),
      color: TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_LATE_8,
      severity: TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_LATE_8,
      filterKey: PerformanceClassificationStatus.LATE_8,
      parentKey: PerformanceClassificationStatus.LATE,
      timeRange: '481-720'
    },
    {
      label: TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_12,
      value: this.getEnumKeyByValue(
        TrainPerformanceClassificationText,
        TrainPerformanceClassificationText.PERFORMANCE_CLASSIFICATION_LATE_12
      ),
      color: TrainPerformanceClassificationColor.PERFORMANCE_CLASSIFICATION_LATE_12,
      severity: TrainPerformanceClassificationSeverity.PERFORMANCE_CLASSIFICATION_LATE_12,
      filterKey: PerformanceClassificationStatus.LATE_12,
      parentKey: PerformanceClassificationStatus.LATE,
      timeRange: '720+'
    }
  ];

  trainStoppageOptions = [
    {
      label: TrainStoppageLabel.Stopped_trains,
      value: 'TRAIN_STOPPED',
      color: TrainStoppageColor.Stopped_trains,
      severity: TrainStoppageSeverity.Stopped_trains,
      filterKey: 'TRAIN_STOPPED',
      all: false
    }
  ];

  constructor() {
    this.resetFilterFormGroup();
  }

  getEnumKeyByValue(enumObj, value) {
    let enumKey = Object.keys(enumObj).find(key => enumObj[key] === value);
    return enumKey;
  }

  /**
   * restores a previously saved default filter into the filter form group.
   */
  setFilterPreferenceResponse(filters: FiltersPreferences | null): void {
    if (!filters) {
      return;
    }
    this.iohFilterFormGroup.patchValue(
      {
        performance:
          filters.performance ||
          this.trainPerformanceOptions.map(() => []),
        lateSubFilters:
          filters.lateSubFilters ||
          this.lateSubFilterOptions.map(() => []),
        commodities:
          filters.commodities ||
          this.commodityFilterOptions.map(() => []),
        trainGroups: filters.trainGroups || '',
        trainStoppage:
          filters.trainStoppage ||
          this.trainStoppageOptions.map(() => []),
        trainLengths:
          filters.trainLengths || this.trainLengthOptions.map(() => [])
      },
      { emitEvent: false }
    );
    this.isIohFilterApplied =
      JSON.stringify(this.initialValues) !==
      JSON.stringify(this.iohFilterFormGroup.getRawValue());
  }

  /**
   * resets the filter form group with default values.
   */
  resetFilterFormGroup() {
    this.iohFilterFormGroup = new FormGroup({
      performance: this.createFormArray(this.trainPerformanceOptions),
      lateSubFilters: this.createFormArray(this.lateSubFilterOptions),
      commodities: this.createCommodityFormArray(
        this.commodityFilterOptions || []
      ),
      trainGroups: new FormControl(''),
      iohInputForSearch: new FormControl(),
      trainStoppage: this.createFormArray(this.trainStoppageOptions),
      trainLengths: this.createFormArray(this.trainLengthOptions)
    });
    this.initialValues = this.iohFilterFormGroup.value;
  }

  /** getter methods to acccess form controls - started */
  get performanceControl() {
    return (this.iohFilterFormGroup.get('performance') as FormArray).controls;
  }

  get lateSubFiltersControl() {
    return (this.iohFilterFormGroup.get('lateSubFilters') as FormArray).controls;
  }

  get commoditiesDelayControl() {
    return (this.iohFilterFormGroup.get('commodities') as FormArray).controls; //this.iohFilterFormGroup.get('');
  }


  get trainStoppageControl() {
    return (this.iohFilterFormGroup.get('trainStoppage') as FormArray).controls;
  }

  get trainLengthsControl() {
    return (this.iohFilterFormGroup.get('trainLengths') as FormArray).controls;
  }

  /** getter methods to acccess form controls - ended */

  /**
   * Create form array based on options
   * @params options
   * @return FormArray
   */
  createFormArray(options: any[]): FormArray {
    const formArray = new FormArray([]);
    options.forEach(_ => formArray.push(new FormControl([])));
    return formArray;
  }

  createCommodityFormArray(options: any[]): FormArray {
    const defaultSelectedCommodityList = ['MERCHANDISE', 'INTERMODAL'];
    const formArray = new FormArray([]);
    options.forEach(option => {
      const value = [];
      if (defaultSelectedCommodityList.includes(option.value)) {
        value.push(option.value);
      }
      formArray.push(new FormControl(value));
    });
    return formArray;
  }

  getCommodityCode(trainClassCode, trainTypeCode) {
    const item =
      this.iohCommodityAllFilterOptions &&
      this.iohCommodityAllFilterOptions.find(
        item =>
          item.trainClassCode === trainClassCode &&
          item.trainTypeCode === trainTypeCode
      );
    if (item) {
      return item.commodityDesc;
    } else {
      return '';
    }
  }

  resolveTrainCommodity(train: any): string {
    if (!train) {
      return '';
    }

    const mappedCommodity = this.getCommodityCode(
      train.trainClassCode,
      train.trainTypeCode
    );

    return String(mappedCommodity || '').trim().toUpperCase();
  }

  isIntermodalTrain(train: any): boolean {
    if (!train) {
      return false;
    }

    const intermodalFlag = train.intermodalTrain;
    const hasIntermodalFlag =
      intermodalFlag === true || intermodalFlag === 'true' || intermodalFlag === 1;

    return hasIntermodalFlag || this.resolveTrainCommodity(train) === 'INTERMODAL';
  }

  getCommodityCodeFromDescription(desc) {
    const commodity = this.iohCommodityAllFilterOptions.find(
      commodity => commodity.commodityDesc === desc
    );
    if (commodity) {
      return commodity.commodityC;
    }
    return '';
  }
}
